import * as R from 'ramda';
import { SaleItemType } from '../../../../../../../../../components/Search/interfaces/product-action.dto';
import { ArrayUtil } from '../../../../../../../../../utils/array.utils';
import {
  saleItemIsKit,
  saleItemIsProductColor,
} from '../../../../../../../../catalogs/utils/saleItemType.utils';
import { AvailabilityEnum } from '../../../../../../../../products/ProductTypes';
import { isPreOrder } from '../../../../../../../../products/utils/availability.utils';
import {
  OfflineDatabaseCatalog,
  OfflineDatabaseCatalogSection,
  OfflineDatabaseCatalogSectionItemFilterOption,
  OfflineDatabaseCatalogSectionKit,
  OfflineDatabaseCatalogSectionProduct,
} from '../../../../../../../database/offlineDatabase/types/offlineDatabaseCatalog.types';
import { OfflineDatabaseProduct } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfoV2 } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineDatabaseSku } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseSku.types';
import { offlineCatalogsRepository } from '../../../../../../../repositories/offlineCatalogs.repository';
import { offlineKitsRepository } from '../../../../../../../repositories/offlineKits.repository';
import { offlineProductColorPriceInfoRepository } from '../../../../../../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../../../../../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../../../../../../repositories/offlineProducts.repository';
import { offlineSkusRepository } from '../../../../../../../repositories/offlineSkus.repository';
import { QueryKeysAndFnOptions } from '../../../useQueryKeysAndFnToCache.types';
import { generateQueryKeysAndFnToPaginatedEndpoint } from '../../generateQueryKeysAndFnToPaginatedEndpoint';
import FilterOptionsMetadata from './entities/FilterOptionsMetadata';
import { QueryKeysAndFnGenerator, QueryKeysAndFnGeneratorParams } from './types';
import {
  CatalogSectionItem,
  FindCatalogsToOfflineOutput,
  GetSectionKitFilterOptionsParams,
  GetSectionProductFilterOptionsParams,
  SectionProductChunkMaps,
  ToOfflineSectionKitParams,
  ToOfflineSectionProductParams,
} from './types/catalogsQueryKeysAndFnGenerator.types';
import { OfflineImageCacher } from './utils/OfflineImageCacher.util';
import { mapKitEntitiesIds } from './utils/mapKitEntitiesIds.utils';

export class CatalogsQueryKeysAndFnGenerator implements QueryKeysAndFnGenerator {
  private readonly CATALOGS_CHUNK_SIZE = 3;
  private readonly SECTIONS_CHUNK_SIZE = 5;
  private readonly SECTION_PRODUCTS_CHUNK_SIZE = 20;
  private readonly IMAGES_CHUNK_SIZE = 10;
  private readonly MAX_IMAGE_CACHE_SIZE_100_MB_IN_BYTES = 100 * 1024 * 1024; // todo: validar quantidade com base em marca grande de prod
  private readonly offlineImageCacher: OfflineImageCacher;

  constructor(
    private readonly params: Pick<QueryKeysAndFnGeneratorParams, 'priority' | 'webpEnabled'>,
  ) {
    this.offlineImageCacher = new OfflineImageCacher(
      this.MAX_IMAGE_CACHE_SIZE_100_MB_IN_BYTES,
      this.IMAGES_CHUNK_SIZE,
      this.params.webpEnabled,
    );
  }

  async generateQueryKeysAndFn(): Promise<QueryKeysAndFnOptions> {
    return generateQueryKeysAndFnToPaginatedEndpoint<FindCatalogsToOfflineOutput>({
      url: '/catalogs/offline/v1',
      priority: this.params.priority,
      fnToSaveData: async (data) => this.handleSaveAll(data),
    });
  }

  private async handleSaveAll(data: FindCatalogsToOfflineOutput[]): Promise<void> {
    const offlineCatalogs = await this.getOfflineCatalogsToSave(data);
    await offlineCatalogsRepository.saveAll(offlineCatalogs);
  }

  private async getOfflineCatalogsToSave(
    data: FindCatalogsToOfflineOutput[],
  ): Promise<OfflineDatabaseCatalog[]> {
    const withCachedImages = await this.cacheCatalogImages(data);
    const offlineCatalogs = await this.toOfflineCatalogs(withCachedImages);
    return offlineCatalogs;
  }

  private async cacheCatalogImages(
    data: FindCatalogsToOfflineOutput[],
  ): Promise<FindCatalogsToOfflineOutput[]> {
    const toCache = data
      .filter((catalog) => !!catalog.imageUrl)
      .map((catalog) => ({
        id: catalog.id,
        url: catalog.imageUrl as string,
      }));

    const { successItemsMap } = await this.offlineImageCacher.cacheImages(toCache, 'md');

    const updatedData = data.map((catalog) => {
      const original = catalog.imageUrl;
      if (!original) {
        return catalog;
      }

      const cached = successItemsMap.get(catalog.id);

      return {
        ...catalog,
        imageUrl: cached ?? original,
      };
    });

    return updatedData;
  }

  private async toOfflineCatalogs(
    data: FindCatalogsToOfflineOutput[],
  ): Promise<OfflineDatabaseCatalog[]> {
    const offlineCatalogs = await ArrayUtil.chunkAndMapSequential({
      items: data,
      chunkSize: this.CATALOGS_CHUNK_SIZE,
      mapper: (catalog) => this.toOfflineCatalog(catalog),
    });
    return offlineCatalogs;
  }

  private async toOfflineCatalog(
    catalog: FindCatalogsToOfflineOutput,
  ): Promise<OfflineDatabaseCatalog> {
    const metadata = new FilterOptionsMetadata();

    const catalogSections = await ArrayUtil.chunkAndMapSequential({
      items: catalog.sections,
      chunkSize: this.SECTIONS_CHUNK_SIZE,
      mapper: (section) => this.toOfflineSection(section, metadata),
    });

    return {
      id: catalog.id,
      name: catalog.name,
      active: catalog.active,
      position: catalog.position,
      imageUrl: catalog.imageUrl,
      startDate: catalog.startDate,
      endDate: catalog.endDate,
      sections: catalogSections,
      isDynamic: catalog.isDynamic,
      customerIds: catalog.customerIds,
      classificationIds: catalog.classificationIds,
      salesRepresentativeIds: catalog.salesRepresentativeIds,
      filterOptionsMetadata: metadata.toOfflineCatalogFilterOptionsMetadata(),
    };
  }

  private async toOfflineSection(
    section: FindCatalogsToOfflineOutput['sections'][0],
    metadata: FilterOptionsMetadata,
  ): Promise<OfflineDatabaseCatalogSection> {
    const sectionItems = await this.getOfflineCatalogSectionItems(section, metadata);

    return {
      id: section.id,
      name: section.name,
      position: section.position,
      isDefault: section.isDefault,
      items: sectionItems,
    };
  }

  private async getOfflineCatalogSectionItems(
    section: FindCatalogsToOfflineOutput['sections'][0],
    metadata: FilterOptionsMetadata,
  ): Promise<OfflineDatabaseCatalogSection['items']> {
    const sectionItemsToMount = await this.getSectionProductsToMount(section);
    const chunks = ArrayUtil.chunk(sectionItemsToMount, this.SECTION_PRODUCTS_CHUNK_SIZE);

    const sectionItems: OfflineDatabaseCatalogSection['items'] = [];

    for (const chunk of chunks) {
      const [mountedSectionProducts, mountedSectionKits] = await Promise.all([
        this.mountCatalogSectionProducts(chunk, metadata),
        this.mountCatalogSectionKits(chunk, metadata),
      ]);
      sectionItems.push(...mountedSectionProducts, ...mountedSectionKits);
    }
    return sectionItems.sort((a, b) => a.position - b.position);
  }

  private async mountCatalogSectionKits(
    catalogSectionItems: CatalogSectionItem[],
    metadata: FilterOptionsMetadata,
  ): Promise<OfflineDatabaseCatalogSectionKit[]> {
    const catalogSectionKits = catalogSectionItems.filter((csi) => csi.type === SaleItemType.KIT);

    if (catalogSectionKits.length === 0) {
      return [];
    }

    const kitIds = catalogSectionKits.map((sectionKit) => sectionKit.kit.id);
    const { kitsMap, pricesMap, productColorsMap, productsMap, skusMap, kitWithRelationsMap } =
      await this.fetchChunkDataAndMapKits(kitIds);

    const mountedSectionKits: OfflineDatabaseCatalogSectionKit[] = await Promise.all(
      catalogSectionKits.map((sectionKit) => {
        return this.toOfflineSectionKit({
          metadata,
          sectionKit,
          maps: { kitsMap, pricesMap, productColorsMap, productsMap, skusMap, kitWithRelationsMap },
        });
      }),
    );

    return mountedSectionKits;
  }

  private async mountCatalogSectionProducts(
    catalogSectionItems: CatalogSectionItem[],
    metadata: FilterOptionsMetadata,
  ) {
    const catalogSectionProducts = catalogSectionItems.filter(
      (csi) => csi.type === SaleItemType.PRODUCT_COLOR,
    );
    const productIds = catalogSectionProducts.map((sectionProduct) => sectionProduct.product.id);
    const productColorIds = catalogSectionProducts.map(
      (sectionProduct) => sectionProduct.productColor.id,
    );
    const maps = await this.fetchChunkDataAndMapProducts(productIds, productColorIds);

    const mountedSectionProducts = await Promise.all(
      catalogSectionProducts.map((sectionProduct) =>
        this.toOfflineSectionProduct({ maps, metadata, sectionProduct }),
      ),
    );

    return mountedSectionProducts;
  }

  private async getSectionProductsToMount(
    section: FindCatalogsToOfflineOutput['sections'][0],
  ): Promise<FindCatalogsToOfflineOutput['sections'][0]['catalogSectionItems']> {
    const productColorIds = section.catalogSectionItems
      .filter((csi) => csi.type === SaleItemType.PRODUCT_COLOR)
      .map((csi) => csi.productColor.id);
    const kitIds = section.catalogSectionItems
      .filter((csi) => csi.type === SaleItemType.KIT)
      .map((csi) => csi.kit.id);

    const existentProductColorIds =
      await offlineProductColorsRepository.getExistentIds(productColorIds);
    const existentKitIds = await offlineKitsRepository.getExistentIds(kitIds);

    const existentSet = new Set([...existentProductColorIds, ...existentKitIds]);

    return section.catalogSectionItems
      .filter(
        (csi) =>
          (saleItemIsProductColor(csi) && existentSet.has(csi.productColor.id)) ||
          (saleItemIsKit(csi) && existentSet.has(csi.kit.id)),
      )
      .map((csi, index) => ({ ...csi, position: index }));
  }

  private async fetchChunkDataAndMapKits(kitIds: string[]) {
    const kitsArray = await offlineKitsRepository.findByIds(kitIds);
    const kitsMap = new Map(kitsArray.map((k) => [k.id, k]));
    const { productColorIds, productIds, skuIds, kitWithRelationsMap } =
      mapKitEntitiesIds(kitsArray);

    const { productColorsMap, productsMap, pricesMap } = await this.fetchChunkDataAndMapProducts(
      productIds,
      productColorIds,
    );

    const skusArray = await offlineSkusRepository.findByIds(skuIds);
    const skusMap = new Map<string, OfflineDatabaseSku[]>();
    skusArray.forEach((sku) => {
      const arr = skusMap.get(sku.productColorId) ?? [];
      arr.push(sku);
      skusMap.set(sku.productColorId, arr);
    });

    return { kitsMap, productColorsMap, productsMap, pricesMap, skusMap, kitWithRelationsMap };
  }

  private async fetchChunkDataAndMapProducts(
    productIds: string[],
    productColorIds: string[],
  ): Promise<SectionProductChunkMaps> {
    const [productsArray, productColorsArray, skusArray, pricesArray] = await Promise.all([
      offlineProductsRepository.findByIds(productIds),
      offlineProductColorsRepository.findByIds(productColorIds),
      offlineSkusRepository.findByProductColorIds(productColorIds),
      offlineProductColorPriceInfoRepository.findAllByProductColorIds(productColorIds),
    ]);

    const productsMap = new Map(productsArray.map((product) => [product.id, product]));
    const productColorsMap = new Map(
      productColorsArray.map((productColor) => [productColor.id, productColor]),
    );

    const skusMap = new Map<string, OfflineDatabaseSku[]>();
    skusArray.forEach((sku) => {
      const arr = skusMap.get(sku.productColorId) ?? [];
      arr.push(sku);
      skusMap.set(sku.productColorId, arr);
    });

    const pricesMap = new Map<string, OfflineDatabaseProductColorPriceInfoV2[]>();
    pricesArray.forEach((price) => {
      const arr = pricesMap.get(price.productColorId) ?? [];
      arr.push(price as OfflineDatabaseProductColorPriceInfoV2);
      pricesMap.set(price.productColorId, arr);
    });

    return { productsMap, productColorsMap, skusMap, pricesMap };
  }

  private async toOfflineSectionProduct({
    maps,
    metadata,
    sectionProduct,
  }: ToOfflineSectionProductParams): Promise<OfflineDatabaseCatalogSectionProduct> {
    const product = maps.productsMap.get(sectionProduct.product.id);
    const productColor = maps.productColorsMap.get(sectionProduct.productColor.id);
    const allSkus = maps.skusMap.get(sectionProduct.productColor.id) ?? [];
    const allPrices = maps.pricesMap.get(sectionProduct.productColor.id) ?? [];

    if (!product || !productColor) {
      throw new Error('erro ao baixar produtos do catálogo');
    }

    const filterOptions = this.getSectionProductFilterOptions({
      product,
      productColor,
      allSkus,
      allPrices: allPrices as OfflineDatabaseProductColorPriceInfoV2[],
    });

    this.updateMetadataWithSectionProductFilterOptions(metadata, filterOptions);
    if (filterOptions.sellInPriceByPriceTable) {
      this.updateMetadataWithSellInPrices(metadata, filterOptions.sellInPriceByPriceTable);
    }

    return {
      id: sectionProduct.id,
      position: sectionProduct.position,
      type: SaleItemType.PRODUCT_COLOR,
      product: { id: product.id },
      productColor: { id: productColor.id },
      filterOptions,
    };
  }

  private async toOfflineSectionKit({ maps, metadata, sectionKit }: ToOfflineSectionKitParams) {
    const kit = maps.kitsMap.get(sectionKit.kit.id);
    if (!kit) {
      throw new Error(
        `erro ao baixar kits do catálogo (sectionKitId=${sectionKit.id}, kitId=${sectionKit.kit.id})`,
      );
    }

    const kitRelations = maps.kitWithRelationsMap.get(kit.id);
    if (!kitRelations) {
      throw new Error(`erro ao baixar kits do catálogo (relations ausentes para kitId=${kit.id})`);
    }

    const { productColorIds, productIds } = kitRelations;
    const productsPromise = Promise.resolve(
      productIds.map((productId) => maps.productsMap.get(productId)).filter((p) => !!p),
    );
    const productColorsPromise = Promise.resolve(
      productColorIds
        .map((productColorId) => maps.productColorsMap.get(productColorId))
        .filter((productColor) => !!productColor),
    );
    const allPricesPromise = Promise.resolve(
      productColorIds
        .flatMap((productColorId) => maps.pricesMap.get(productColorId) ?? [])
        .filter((price) => !!price),
    );

    const [products, productColors, allPrices] = await Promise.all([
      productsPromise,
      productColorsPromise,
      allPricesPromise,
    ]);

    const allSkus = productColorIds
      .flatMap((skuId) => maps.skusMap.get(skuId))
      .filter((sku) => !!sku);
    const filterOptions = this.getSectionKitFilterOptions({
      kit,
      allSkus,
      products,
      productColors,
      allPrices: allPrices as OfflineDatabaseProductColorPriceInfoV2[],
    });

    this.updateMetadataWithSectionProductFilterOptions(metadata, filterOptions);
    if (filterOptions.sellInPriceByPriceTable) {
      this.updateMetadataWithSellInPrices(metadata, filterOptions.sellInPriceByPriceTable);
    }

    return {
      id: sectionKit.id,
      position: sectionKit.position,
      type: SaleItemType.KIT as SaleItemType.KIT,
      kit: { id: kit.id },
      filterOptions,
    };
  }

  private computeItemPriceFilters(allPrices: OfflineDatabaseProductColorPriceInfoV2[]): {
    priceTableIdsWithZeroPrice: Set<string>;
    priceTableIdsWithPromotion: Set<string>;
    sellInPriceByPriceTable: Record<string, { min: number; max: number }>;
  } {
    const priceTableIdsWithZeroPrice = new Set<string>();
    const priceTableIdsWithPromotion = new Set<string>();
    const sellInPriceByPriceTable: Record<string, { min: number; max: number }> = {};

    allPrices.forEach((priceInfo) => {
      if (priceInfo.pricesToSizes.some((p) => p.promotionalPrice !== undefined)) {
        priceTableIdsWithPromotion.add(priceInfo.priceTableId);
      }
      if (priceInfo.pricesToSizes.every((p) => p.wholesalePrice === 0)) {
        priceTableIdsWithZeroPrice.add(priceInfo.priceTableId);
      }

      priceInfo.pricesToSizes.forEach((priceToSize) => {
        const effectivePrice =
          priceToSize.promotionalPrice !== undefined &&
          priceToSize.promotionalPrice < priceToSize.wholesalePrice
            ? priceToSize.promotionalPrice
            : priceToSize.wholesalePrice;

        const current = sellInPriceByPriceTable[priceInfo.priceTableId];
        if (!current) {
          sellInPriceByPriceTable[priceInfo.priceTableId] = {
            min: effectivePrice,
            max: effectivePrice,
          };
        } else {
          current.min = Math.min(current.min, effectivePrice);
          current.max = Math.max(current.max, effectivePrice);
        }
      });
    });

    return { priceTableIdsWithZeroPrice, priceTableIdsWithPromotion, sellInPriceByPriceTable };
  }

  private getSectionKitFilterOptions({
    kit,
    allPrices,
    allSkus,
    productColors,
    products,
  }: GetSectionKitFilterOptionsParams): OfflineDatabaseCatalogSectionKit['filterOptions'] {
    const { priceTableIdsWithZeroPrice, priceTableIdsWithPromotion, sellInPriceByPriceTable } =
      this.computeItemPriceFilters(allPrices);

    const productColorFilters = this.getCatalogSectionKitProductColorFilters(productColors);
    const productFilters = this.getCatalogSectionKitProductFilters(products);
    const skuFilters = this.getCatalogSectionKitSkuFilters(allSkus);
    const kitHasMaterialPreOrder = kit.skuKits.some(
      (sk) => sk.availability === AvailabilityEnum.MATERIAL_PRE_ORDER,
    );
    const kitHasInfinitePreOrder = kit.skuKits.some(
      (sk) =>
        sk.availability !== AvailabilityEnum.MATERIAL_PRE_ORDER && isPreOrder(sk.availability),
    );
    const kitHasReadyForDelivery = kit.skuKits.some(
      (sk) => sk.availability === AvailabilityEnum.READY_FOR_DELIVERY,
    );
    const availabilities: AvailabilityEnum[] = [
      ...(kitHasInfinitePreOrder ? [AvailabilityEnum.INFINITE_PRE_ORDER] : []),
      ...(kitHasMaterialPreOrder ? [AvailabilityEnum.MATERIAL_PRE_ORDER] : []),
      ...(kitHasReadyForDelivery ? [AvailabilityEnum.READY_FOR_DELIVERY] : []),
    ];

    return {
      ...productColorFilters,
      ...productFilters,
      ...skuFilters,
      availabilities,
      itemCodeAndName: [kit.code, kit.name],
      priceTableIdsWithPromotion: Array.from(priceTableIdsWithPromotion),
      priceTableIdsWithZeroPrice: Array.from(priceTableIdsWithZeroPrice),
      sellInPriceByPriceTable,
    };
  }

  private getSectionProductFilterOptions({
    product,
    allPrices,
    allSkus,
    productColor,
  }: GetSectionProductFilterOptionsParams): OfflineDatabaseCatalogSectionProduct['filterOptions'] {
    const { priceTableIdsWithZeroPrice, priceTableIdsWithPromotion, sellInPriceByPriceTable } =
      this.computeItemPriceFilters(allPrices);

    return {
      colorIds: [productColor.colorId],
      categoryIds: !product.categoryId ? [] : [product.categoryId],
      collectionIds: [product.collectionId],
      itemCodeAndName: [product.code, product.name],
      deliveryWindowIds: R.uniq([
        ...productColor.deliveryWindowIds.preOrder,
        ...productColor.deliveryWindowIds.futureStock,
      ]),
      referenceColorIds: productColor.referenceColorIds,
      classificationIds: productColor.classificationIds,
      availabilities: productColor.availabilities,
      sizeIds: allSkus.map((sku) => sku.sizeId),
      priceTableIdsWithPromotion: Array.from(priceTableIdsWithPromotion),
      priceTableIdsWithZeroPrice: Array.from(priceTableIdsWithZeroPrice),
      sellInPriceByPriceTable,
    };
  }

  private updateMetadataWithSectionProductFilterOptions(
    metadata: FilterOptionsMetadata,
    filterOptions:
      | OfflineDatabaseCatalogSectionProduct['filterOptions']
      | OfflineDatabaseCatalogSectionKit['filterOptions'],
  ): void {
    metadata.addFrom(filterOptions);
  }

  private updateMetadataWithSellInPrices(
    metadata: FilterOptionsMetadata,
    sellInPriceByPriceTable: Record<string, { min: number; max: number }>,
  ): void {
    Object.entries(sellInPriceByPriceTable).forEach(([priceTableId, range]) => {
      metadata.mergeSellInPriceRange(priceTableId, range);
    });
  }

  private getCatalogSectionKitSkuFilters(
    allSkus: OfflineDatabaseSku[],
  ): Pick<OfflineDatabaseCatalogSectionItemFilterOption, 'sizeIds'> {
    const sizeIds = new Set<string>();
    allSkus.forEach((sku) => sizeIds.add(sku.sizeId));
    return {
      sizeIds: Array.from(sizeIds),
    };
  }

  private getCatalogSectionKitProductFilters(
    products: OfflineDatabaseProduct[],
  ): Pick<OfflineDatabaseCatalogSectionItemFilterOption, 'categoryIds' | 'collectionIds'> {
    const productFilters = products.reduce(
      (acc, p) => {
        acc.collectionIds.add(p.collectionId);
        p.categoryId && acc.categoryIds.add(p.categoryId);
        return acc;
      },
      { categoryIds: new Set<string>(), collectionIds: new Set<string>() },
    );

    return {
      categoryIds: Array.from(productFilters.categoryIds),
      collectionIds: Array.from(productFilters.collectionIds),
    };
  }
  private getCatalogSectionKitProductColorFilters(
    productColors: OfflineDatabaseProductColor[],
  ): Pick<
    OfflineDatabaseCatalogSectionItemFilterOption,
    'colorIds' | 'referenceColorIds' | 'classificationIds' | 'deliveryWindowIds'
  > {
    const productColorFilters = productColors.reduce(
      (acc, productColor) => {
        acc.colorIds.add(productColor.colorId);
        productColor.referenceColorIds.forEach((id) => acc.referenceColorIds.add(id));
        productColor.classificationIds.forEach((id) => acc.classificationIds.add(id));
        [
          ...productColor.deliveryWindowIds.preOrder,
          ...productColor.deliveryWindowIds.futureStock,
        ].forEach((id) => acc.deliveryWindowIds.add(id));

        return acc;
      },
      {
        colorIds: new Set<string>(),
        referenceColorIds: new Set<string>(),
        classificationIds: new Set<string>(),
        deliveryWindowIds: new Set<string>(),
      },
    );

    return {
      colorIds: Array.from(productColorFilters.colorIds),
      referenceColorIds: Array.from(productColorFilters.referenceColorIds),
      classificationIds: Array.from(productColorFilters.classificationIds),
      deliveryWindowIds: Array.from(productColorFilters.deliveryWindowIds),
    };
  }
}
